import type { GridApiPremium, GridColDef, GridDataSource, GridRowParams } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { FileDownloadInfo } from "@entrilia/shared/api/fileTypes";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { GRID_DEFAULT_PAGE_SIZE } from "@entrilia/shared/components/grid/gridHelper";
import InlineItemsList from "@entrilia/shared/components/InlineItemsList";
import PreviewFileDialog from "@entrilia/shared/components/previewFile/PreviewFileDialog";
import CompletionPercentageIndicator from "../../../common/CompletionPercentageIndicator";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { distinct } from "@entrilia/shared/utilities/arrayHelper";
import { convertISODate, convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { formatDurationInSeconds } from "@entrilia/shared/utilities/formatters";
import type { DocumentViews } from "../../../../api/types/documentActivityTypes";
import storage from "../../../../storage/storage";
import CategoryInlineTags from "../../../common/CategoryInlineTags";
import { getDocumentViewFileDownloadInfo } from "./documentViewFileHelper";
import DocumentViewsGridActionsCell from "./DocumentViewsGridActionsCell";
import { DocumentViewsGridContextProvider } from "./DocumentViewsGridContextProvider";
import DocumentViewsGridFileNameCell from "./DocumentViewsGridFileNameCell";
import DocumentViewsGridRowDetail from "./DocumentViewsGridRowDetail";
import DocumentViewsGridUserCell from "./DocumentViewsGridUserCell";

// Using Partial type here as row will be substituted with placeholder model during lazy loading, so all its fields may become undefined
const isDetailPanelAvailable = (row: Partial<DocumentViews>) =>
  row.pageViews != null && Object.values(row.pageViews).some((val) => val > 0);

const columnDefinitions: GridColDef<DocumentViews>[] = [
  {
    field: "userInfo",
    headerName: "Contact",
    flex: 1,
    minWidth: 160,
    cellClassName: "highlighted-action",
    renderCell: ({ row }) => <DocumentViewsGridUserCell row={row} />,
  },
  {
    field: "documentInfo.investorNames",
    headerName: "Investor",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (row.documentInfo.investorNames.length === 0) {
        return "-";
      }

      return (
        <InlineItemsList<string>
          displayCount={1}
          items={distinct(row.documentInfo.investorNames)}
          propGetter={(name) => name}
        />
      );
    },
  },
  {
    field: "documentInfo.fundNames",
    headerName: "Fund",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => {
      if (row.documentInfo.fundNames.length === 0) {
        return "-";
      }

      return (
        <InlineItemsList<string>
          displayCount={1}
          items={distinct(row.documentInfo.fundNames)}
          propGetter={(name) => name}
        />
      );
    },
  },
  {
    field: "documentInfo.categories",
    headerName: "Category",
    flex: 1,
    minWidth: 160,
    renderCell: ({ row }) => (
      <CategoryInlineTags categoryIds={row.documentInfo.categoryIds} maxDisplayedCategories={2} noWrap />
    ),
  },
  {
    field: "originatorNo",
    headerName: "Collection ID",
    type: "number",
    flex: 0.5,
    minWidth: 100,
  },
  {
    field: "documentInfo",
    headerName: "Document",
    flex: 1.5,
    minWidth: 160,
    cellClassName: "highlighted-action",
    renderCell: ({ row }) => <DocumentViewsGridFileNameCell row={row} />,
  },
  {
    field: "documentInfo.publishedAt",
    headerName: "Publish Date",
    width: 120,
    renderCell: ({ row }) => convertISODate(row.documentInfo.publishedAt),
  },
  {
    field: "sessionStart",
    headerName: "Viewed at",
    width: 152,
    valueFormatter: (value: string) => convertISODateTime(value),
  },
  {
    field: "totalViewDuration",
    headerName: "View Duration",
    width: 110,
    headerAlign: "right",
    align: "right",
    valueFormatter: (value: number) => (value ? formatDurationInSeconds(value) : ""),
  },
  {
    field: "completionPercentage",
    headerName: "Total Completion",
    type: "number",
    width: 130,
    headerAlign: "center",
    align: "center",
    renderCell: ({ row }) => {
      if (!row.completionPercentage) {
        return null;
      }

      return <CompletionPercentageIndicator value={row.completionPercentage} />;
    },
  },
  {
    field: "lastDownloadTime",
    headerName: "Downloaded at",
    width: 152,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    cellClassName: "grid-row-actions",
    align: "right",
    headerAlign: "right",
    renderCell: ({ row }) => <DocumentViewsGridActionsCell row={row} />,
  },
];

const hiddenContactColumnVisibilityModel = { userInfo: false };

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  dataSource: GridDataSource;
  filterId: string;
  hideContactColumn?: boolean;
}

const getFileDownloadInfo = withErrorHandling(getDocumentViewFileDownloadInfo);

const DocumentViewsGrid = ({ apiRef, dataSource, filterId, hideContactColumn }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [previewFileInfo, setPreviewFileInfo] = useState<FileDownloadInfo>();

  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<DocumentViews>) =>
      isDetailPanelAvailable(row) ? <DocumentViewsGridRowDetail row={row} /> : undefined,
    []
  );

  const getDetailPanelHeight = useCallback(() => 320, []);

  const handleShowFilePreview = useCallback(
    async (documentView: DocumentViews) => {
      const [fileInfo, error] = await getFileDownloadInfo(documentView);
      if (error?.type === "NotFound") {
        sendNotification(
          "The file you are trying to view is no longer available. It was previously accessible, but it has now been removed."
        );
        return;
      } else if (error) {
        sendNotificationError(error.message);
        logError(error, `[DocumentViewsGrid] getDocumentViewFileDownloadInfo ${documentView.id}`);
        return;
      }

      setPreviewFileInfo(fileInfo);
    },
    [sendNotification, sendNotificationError]
  );

  const [dataSourceError, setDataSourceError] = useState<Error>();

  const handleDataSourceError = useCallback(
    (error: Error) => {
      logError(error, "[DocumentViewsGrid]");
      setDataSourceError(error);
      storage.clearFilterState(filterId);
    },
    [filterId]
  );

  if (dataSourceError) {
    return <DataLoadingFailed title="Loading document activity failed" />;
  }

  return (
    <DocumentViewsGridContextProvider showFilePreview={handleShowFilePreview}>
      <DataGrid<DocumentViews>
        apiRef={apiRef}
        columns={columnDefinitions}
        columnVisibilityModel={hideContactColumn ? hiddenContactColumnVisibilityModel : undefined}
        lazyLoading
        dataSource={dataSource}
        paginationModel={{ page: 0, pageSize: GRID_DEFAULT_PAGE_SIZE }}
        onDataSourceError={handleDataSourceError}
        multilineRows
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={getDetailPanelHeight}
        noRowsText="No document activity"
        disableColumnSorting
        disableColumnReorder
      />

      {previewFileInfo?.downloadUrl && (
        <PreviewFileDialog
          url={previewFileInfo.downloadUrl}
          fileName={previewFileInfo.fileName ?? ""}
          onClose={() => setPreviewFileInfo(undefined)}
        />
      )}
    </DocumentViewsGridContextProvider>
  );
};

export default DocumentViewsGrid;
